import { ChangeEvent } from 'react';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { CoreBox, CoreCheckbox, CoreFormControlLabel, CoreTypography } from '@youscience/core';
import {
  OptionalReportRequest,
  OptionalReportSchema,
  YesNo,
  TranscriptGradeInclusionMRFR,
} from '@youscience/commonapp-service-common';

import { FormState } from '@constants/commonApp/types';
import { REQUIRED_FIELD_MESSAGE } from '@constants/texts';

import SectionAccordion from '@components/SectionAccordion';
import ContinueButton from '@components/ContinueButton';

import { getFilename } from '../../../../common/formUtils';
import FileUpload from '../../../../common/FileUpload';

import { sxStyles } from './Transcripts.styles';
import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';
import { TabSections } from '@interfaces/recommenderWorkspace';

export type TranscriptsFields = NonNullable<Partial<OptionalReportRequest[TabSections.Transcripts]>>;

interface TranscriptsProps {
  transcripts?: TranscriptsFields;
  onFormChange: (formState: FormState<TranscriptsFields>) => void;
  onExpand: () => void;
  onContinue?: () => void;
  isExtended: boolean;
  isSaved: boolean;
  applicantName?: string;
  showError?: boolean;
}

const validator = OptionalReportSchema._def.schema.shape.transcripts;

export const Transcripts = ({
  transcripts = {},
  onFormChange,
  onExpand,
  onContinue,
  isExtended,
  isSaved,
  applicantName = '',
  showError = false,
}: TranscriptsProps) => {
  const [formState, setFormStateWithErrors] = useApplicantFormSection<TranscriptsFields, typeof validator>(
    transcripts,
    validator,
    isSaved,
    onFormChange,
  );

  const isShowErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';

  const handleSelectChange = (field: string) => (event: SelectChangeEvent<unknown>) => {
    setFormStateWithErrors({ [field]: (event.target.value as string) || '' });
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setFormStateWithErrors({ [event.target.name]: checked ? YesNo.Yes : undefined });
  };

  const handleFileChange = (field: string) => async (file: string | undefined) => {
    setFormStateWithErrors({ [field]: file });
  };

  return (
    <SectionAccordion
      title="Transcripts"
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && !!formState.isFormValid}
      isError={showError || !formState.isFormValid}
    >
      <CoreBox sx={sxStyles.accordionDetails}>
        <CoreBox sx={sxStyles.flexWrap}>
          <CoreTypography variant="subtitle2" component="h4">
            This is not a substitute for the Midyear or Final Report. Please use this only if you wish to update grades
            at another point in the year. Attach applicant's current or most recent official transcript, including
            courses in progress and a transcript legend.
          </CoreTypography>

          <FileUpload
            title="Upload current or most recent transcript"
            name="currentTranscript"
            initialFile={formState.data?.currentTranscript}
            previewTitle="Current Transcript"
            filename={formState.data?.currentTranscript ? getFilename(applicantName, 'current_transcript') : undefined}
            onFileUpload={handleFileChange}
            onFileDelete={handleFileChange}
            error={formState.errors.currentTranscript}
          />
        </CoreBox>

        <CoreBox sx={sxStyles.flexWrap}>
          <CoreTypography variant="subtitle2" component="h4">
            Transcript affirmation
          </CoreTypography>

          <CoreFormControlLabel
            sx={sxStyles.checkbox}
            control={
              <CoreCheckbox
                data-testid="affirmation"
                name="affirmation"
                checked={!!formState?.data?.affirmation}
                onChange={handleCheckboxChange}
              />
            }
            label="I affirm that I have uploaded a transcript or other document listing coursework and available grades/marks to this Optional Report. I understand that failure to do so (by, for example, uploading a blank document or uploading a document promising to mail the transcript at a later time) will result in my Common App Online account being closed."
          />

          {formState.errors.affirmation && <FormHelperText error>{REQUIRED_FIELD_MESSAGE}</FormHelperText>}
        </CoreBox>

        <CoreBox sx={sxStyles.flexWrap}>
          <CoreTypography variant="subtitle2" component="h4">
            The most recent grades included
          </CoreTypography>

          <FormControl fullWidth error={!!formState.errors.grades}>
            <InputLabel required>
              Please indicate the most recent grades included on the transcript accompanying this form
            </InputLabel>

            <Select
              label="Please indicate the most recent grades included on the transcript accompanying this form"
              value={formState?.data?.grades ?? ''}
              onChange={handleSelectChange('grades')}
            >
              {Object.entries(TranscriptGradeInclusionMRFR).map(([key, value]) => {
                return (
                  <MenuItem key={value} value={value}>
                    {key}
                  </MenuItem>
                );
              })}
            </Select>

            {formState.errors.grades && <FormHelperText error>{REQUIRED_FIELD_MESSAGE}</FormHelperText>}
          </FormControl>
        </CoreBox>

        {onContinue && <ContinueButton onContinue={onContinue} />}
      </CoreBox>
    </SectionAccordion>
  );
};
